import { createSlice, createDraftSafeSelector, PayloadAction } from '@reduxjs/toolkit';

interface IDonorFile {
  regDt: string;
  fileNm: string;
  fileSize: number;
  filePath: string;
  placeId: string;
  fileType: string;
  fileId: string;
  thumbFileSize: number;
  thumbFilePath: string;
  thumbFileHash: number;
}

export interface IDonorItem {
  donorId: string;
  delId?: string | null;
  useYn?: string;
  regId?: string;
  regDt?: string;
  placeId?: string;
  donorDesc?: string;
  modId?: string;
  modDt?: string;
  donorNm?: string;
  delYn?: string;
  donorActivity?: string;
  representDonationId?: string | null;
  representDonationNm?: string | null;
  donorFileList?: IDonorFile[];
}

export interface IDonationItem {
  donationId: string;
  donationNm: string;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface ListSuccessPayload {
  donorList: IDonorItem[];
  pagination: Pagination;
}

interface DonorState {
  donorList: IDonorItem[];
  donationList: IDonationItem[];
  donorInfo?: IDonorItem | null;
  pagination?: Pagination;
  actionResult: string;
  isLoading: boolean;
  error: string | null;
}

const initialState: DonorState = {
  donorList: [],
  donationList: [],
  donorInfo: {},
  actionResult: '',
  isLoading: false,
  error: null,
  pagination: {
    currentPage: 0,
    totalPages: 0,
    totalCount: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
  },
};

const slice = createSlice({
  name: 'donor',
  initialState,
  reducers: {
    stateClear: state => {
      state.donorList = [];
      state.actionResult = '';
      state.isLoading = false;
      state.error = null;
    },
    list: (state: DonorState) => {
      state.isLoading = true;
      state.actionResult = 'LIST_REQ';
      state.error = null;
    },
    listSuccess: (state: DonorState, { payload }: PayloadAction<ListSuccessPayload>) => {
      state.donorList = payload.donorList;
      state.pagination = payload.pagination;
      state.isLoading = false;
      state.actionResult = 'LIST_OK';
      state.error = null;
    },
    listFailure: (state: DonorState, { payload }: PayloadAction<string>) => {
      state.isLoading = false;
      state.actionResult = 'LIST_ERR';
      state.error = payload;
    },
    donorOfDonationList: (state: DonorState) => {
      state.isLoading = true;
      state.actionResult = 'SEARCH_LIST_REQ';
      state.error = null;
    },
    donorOfDonationListSuccess: (state: DonorState, { payload }: PayloadAction<IDonationItem[]>) => {
      state.donationList = payload;
      state.isLoading = false;
      state.actionResult = 'SEARCH_LIST_OK';
      state.error = null;
    },
    donorOfDonationListFailure: (state: DonorState, { payload }: PayloadAction<string>) => {
      state.isLoading = false;
      state.actionResult = 'SEARCH_LIST_ERR';
      state.error = payload;
    },
    add: (state: DonorState, { payload }: PayloadAction<{}>) => {
      state.isLoading = true;
      state.actionResult = 'ADD_REQ';
      state.error = '';
    },
    addSuccess: (state: DonorState, { payload }: PayloadAction<DonorState>) => {
      state.isLoading = false;
      state.actionResult = 'ADD_OK';
      state.error = '';
    },
    addFailure: (state: DonorState, { payload }: PayloadAction<string>) => {
      state.isLoading = false;
      state.actionResult = 'ADD_ERR';
      state.error = payload;
    },
    detail: (state: DonorState, { payload }: PayloadAction<DonorState>) => {
      state.isLoading = true;
      state.actionResult = 'DETAIL_REQ';
      state.error = '';
    },
    detailSuccess: (state: DonorState, { payload }: PayloadAction<DonorState>) => {
      state.donorInfo = payload.donorInfo;
      state.isLoading = false;
      state.actionResult = 'DETAIL_OK';
      state.error = '';
    },
    detailFailure: (state: DonorState, { payload }: PayloadAction<DonorState>) => {
      state.isLoading = false;
      state.actionResult = 'DETAIL_ERR';
      state.error = payload.error;
    },
    update: (state: DonorState, { payload }: PayloadAction<CsutomerDonorState>) => {
      state.isLoading = true;
      state.actionResult = 'UPDATE_REQ';
      state.error = '';
    },
    updateSuccess: (state: DonorState, { payload }: PayloadAction<DonorState>) => {
      state.isLoading = false;
      state.actionResult = 'UPDATE_OK';
      state.error = '';
    },
    updateFailure: (state: DonorState, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.actionResult = 'UPDATE_ERR';
      state.error = action.payload;
    },
    remove: (state: DonorState, { payload }: PayloadAction<CsutomerDonorState>) => {
      state.isLoading = true;
      state.actionResult = 'REMOVE_REQ';
      state.error = '';
    },
    removeSuccess: (state: DonorState, { payload }: PayloadAction<DonorState>) => {
      state.isLoading = false;
      state.actionResult = 'REMOVE_OK';
      state.error = '';
    },
    removeFailure: (state: DonorState, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.actionResult = 'REMOVE_ERR';
      state.error = action.payload;
    },
    actionResultClear: (state: DonorState) => {
      state.actionResult = '';
    },
  },
});

// selectors
const selectDonorList = createDraftSafeSelector(
  (state: DonorState) => state.donorList,
  donorList => donorList,
);

const selectDonationList = createDraftSafeSelector(
  (state: DonorState) => state.donationList,
  donationList => donationList,
);

const selectPaginationList = createDraftSafeSelector(
  (state: DonorState) => state.pagination,
  pagination => pagination,
);

const selectDonorInfo = createDraftSafeSelector(
  (state: DonorState) => state.donorInfo,
  donorInfo => donorInfo,
);

const selectStatus = createDraftSafeSelector(
  (state: DonorState) => state.actionResult,
  (state: DonorState) => state.isLoading,
  (state: DonorState) => state.error,
  (actionResult, isLoading, error) => ({
    actionResult,
    isLoading,
    error,
  }),
);

export const donorSelector = {
  donorList: (state: any) => selectDonorList(state[DONOR]),
  donationList: (state: any) => selectDonationList(state[DONOR]),
  pagination: (state: any) => selectPaginationList(state[DONOR]),
  donorInfo: state => selectDonorInfo(state[DONOR]),
  status: (state: any) => selectStatus(state[DONOR]),
};

export const DONOR = slice.name;
export const donorReducer = slice.reducer;
export const donorAction = slice.actions;
